import { useAuthStore } from "@/store/auth.store";
import type { Button } from "../button";
import { PlanName, ValidationSuscriptionButton } from "@/common/enums/enums";
import { t } from "i18next";
import { TooltipButton } from "./TooltipButton";
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useRecordingUsageStore } from "@/store/recordingUsage.store";
import { AppConfig } from "@/common/appConfig";

type ButtonProps = React.ComponentProps<typeof Button>;

interface Props
  extends Pick<
    ButtonProps,
    "onClick" | "size" | "className" | "disabled" | "type" | "variant"
  > {
  children?: React.ReactNode;
  validations?: ValidationSuscriptionButton[];
}

export const SubscriptionOnlyButton = ({
  validations = [],
  ...props
}: Props): React.ReactNode => {
  const { userData, loading: loadingAuth } = useAuthStore.getState();
  const {
    loading: loadingRecording,
    getAvailablePlanMinutes,
    getAvailableAdditionalMinutes,
  } = useRecordingUsageStore();
  const [messageTooltip, setMessageTooltip] = useState<string>(
    t("components.subscriptionOnlyButton.notAvailable")
  );
  const [canOpenButton, setCanOpenButton] = useState<boolean>(
    validations.length == 0 ? true : false
  );

  useEffect(() => {
    const validationMap = {
      [ValidationSuscriptionButton.PLAN_FREE_TRIAL]: {
        canOpen:
          AppConfig.disablePremiumRestrictions ||
          userData?.planName != PlanName.FREE_TRIAL,
        message: t("components.subscriptionOnlyButton.validationPlanFreeTrial"),
      },
      [ValidationSuscriptionButton.RECORDING_USAGES]: {
        canOpen:
          getAvailablePlanMinutes() + getAvailableAdditionalMinutes() > 0,
        message: t(
          "components.subscriptionOnlyButton.validationRecordingUsages"
        ),
      },
    };

    for (const validation of validations) {
      if (validation in validationMap) {
        setCanOpenButton(validationMap[validation].canOpen);
        setMessageTooltip(validationMap[validation].message);
        if (!validationMap[validation].canOpen) {
          break;
        }
      }
    }
  }, [
    userData,
    loadingRecording,
    validations,
    getAvailablePlanMinutes,
    getAvailableAdditionalMinutes,
  ]);

  const disabledButton = (): boolean => {
    if (loadingAuth && loadingRecording) {
      return true;
    } else {
      if (canOpenButton) {
        return props.disabled || false;
      }
      return true;
    }
  };

  return (
    <TooltipButton
      {...props}
      disabled={disabledButton()}
      tooltip={
        canOpenButton ? null : <Link to="/settings">{messageTooltip}</Link>
      }
    >
      {props.children}
    </TooltipButton>
  );
};
